import React, { Suspense } from 'react';

interface ShikiHighlighterProps {
  children: string;
  language: string;
  theme?: 'light' | 'dark' | 'auto';
  className?: string;
  PreTag?: React.ElementType;
}

// Lazy-loaded Shiki component
const LazyShikiHighlighter = React.lazy(() =>
  import('./shiki-highlighter').then((module) => ({
    default: module.ShikiHighlighter,
  })),
);

// Fallback component while Shiki loads
const ShikiFallback: React.FC<
  Pick<ShikiHighlighterProps, 'children' | 'className' | 'PreTag'>
> = ({ children, className = '', PreTag = 'pre' }) => (
  <PreTag className={`${className} shiki-loading`}>
    <code>{children}</code>
  </PreTag>
);

// Main component that handles the lazy loading
export const ShikiHighlighterLazy: React.FC<ShikiHighlighterProps> = (
  props,
) => {
  // Load Shiki immediately when the component mounts (but still lazy-loaded)
  // This removes Shiki from the initial bundle while still providing good UX
  return (
    <Suspense fallback={<ShikiFallback {...props} />}>
      <LazyShikiHighlighter {...props} />
    </Suspense>
  );
};

// Re-export the useTheme hook for compatibility
export const useTheme = () => {
  const [isDark, setIsDark] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDark(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mediaQuery.addEventListener('change', handler);

    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return isDark ? 'dark' : 'light';
};
